import { Component, inject, signal, computed, OnInit } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { Role } from '@shared/models/entities/roles.model';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';

@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule
],
  templateUrl: './profile-dialog.component.html',
  styleUrl: './profile-dialog.component.scss',
})
export class ProfileDialogComponent implements OnInit {

  private readonly fb               = inject(FormBuilder);
  private readonly usersService     = inject(UsersService);
  private readonly rolesService     = inject(RolesService);
  private readonly messageService   = inject(CustomMessageService);
  private readonly dialogRef        = inject(MatDialogRef<ProfileDialogComponent>);
  private readonly dialog           = inject(MatDialog);

  readonly user = computed(() => this.usersService.currentUser());

  // Imagen actualmente visible en pantalla (siempre es una presigned URL).
  // En BD se persiste el `imagePath` (key S3) — NO esta URL.
  imageUrl = signal<string | null>(null);

  // Path S3 (key) de la imagen — lo que se persiste en BD para no expirar.
  // Cuando suben una imagen nueva, se actualiza desde la respuesta del upload.
  private imagePath: string | null = null;

  // Nombre del rol (resuelto consultando roles).
  roleName = signal<string>('');

  // ¿El rol del usuario es de ingeniero (biomédico)? Se resuelve por el
  // NOMBRE del rol (no por el id): se habilita el "Número de ingeniero"
  // cuando el nombre del rol contiene "ingeniero" (sin acentos / case-insensitive).
  isBiomedicalEngineer = computed(() => {
    const n = (this.roleName() ?? '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return n.includes('ingeniero');
  });

  // Estado de carga de la foto (durante subida) y de guardado.
  isUploading = signal(false);
  isSaving    = signal(false);

  // Marca si se cambió la imagen para incluirla en el patch.
  imageChanged = signal(false);

  // Marca si guardó algún cambio. Se devuelve al cerrar para que home recargue.
  private savedAny = false;

  form!: FormGroup;

  ngOnInit(): void {
    this.buildForm();
    // El currentUser ya trae la presigned URL fresca resuelta por el service.
    this.imageUrl.set(this.user()?.image ?? null);
    this.loadRoleName();
  }

  private buildForm(): void {
    const user = this.user();
    this.form = this.fb.group({
      name:      [user?.name ?? '', [Validators.required, Validators.minLength(2)]],
      phone:     [user?.phone ?? '', [Validators.pattern(/^[+()\d\s-]{0,20}$/)]],
      area:      [user?.area ?? ''],
      employeeNumber: [user?.employeeNumber ?? ''],
      engineerNumber: [user?.engineerNumber ?? ''],
    });
  }

  private async loadRoleName(): Promise<void> {
    const roleId = this.user()?.role;
    if (!roleId) {
      this.roleName.set('Sin rol asignado');
      return;
    }
    try {
      await this.rolesService.search({
        searchType: 'default',
        condition: { id: roleId },
      });
      const roles = this.rolesService.listData();
      this.roleName.set(roles.length > 0 ? (roles[0] as Role).name : roleId);
    } catch {
      this.roleName.set(roleId);
    }
  }

  // ─── Foto ────────────────────────────────────────────────────────────────

  /**
   * Abre el gestor de archivos para la foto de perfil (cargar / ver /
   * reemplazar / quitar). Sube a S3 al path `users/{userId}` (sin folio).
   * NO persistimos en la BD aún — eso pasa al hacer "Guardar cambios".
   */
  async onPickFile(): Promise<void> {
    if (this.isUploading() || this.isSaving()) return;
    const userId = this.user()?.id;
    if (!userId) {
      await this.messageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo identificar el usuario.',
        dismissBtn: true,
      });
      return;
    }
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:      this.imagePath ?? '',
        label:      'Foto de perfil',
        accept:     'image/*',
        uploadPath: `users/${userId}`,   // el API antepone la carpeta del ambiente (dev/ o prod/)
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe((result: string | null) => {
      if (result === null || result === undefined) return; // cancelado
      // Guardamos el `filename` (path S3) — eso es lo que persistiremos
      // en BD al hacer "Guardar" para que NO caduque en 1h.
      this.imagePath = result || null;
      this.imageChanged.set(true);
      if (result) {
        this.usersService.resolveImageUrl(result).then(u => this.imageUrl.set(u));
      } else {
        this.imageUrl.set(null);
      }
    });
  }

  // ─── Guardar / cerrar ────────────────────────────────────────────────────

  async onSave(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const current = this.user();

    const newName  = (this.form.value.name as string).trim();
    const newPhone = ((this.form.value.phone as string) ?? '').trim();
    const newArea  = ((this.form.value.area as string) ?? '').trim();

    const currentName  = current?.name ?? '';
    const currentPhone = current?.phone ?? '';
    const currentArea  = (current?.area ?? '') as string;

    const nameChanged  = newName.toUpperCase() !== currentName.toUpperCase();
    const phoneChanged = newPhone !== (currentPhone ?? '');
    const areaChanged  = newArea !== currentArea;

    // ── Datos profesionales ──────────────────────────────────────────────
    const newEmp = ((this.form.value.employeeNumber as string) ?? '').trim();
    const curEmp = (current?.employeeNumber ?? '') as string;
    const empChanged = newEmp !== curEmp;

    const newEng = ((this.form.value.engineerNumber as string) ?? '').trim();
    const curEng = (current?.engineerNumber ?? '') as string;
    // Solo se considera cambio si el rol es de ingeniero (el campo se muestra).
    const engChanged = this.isBiomedicalEngineer() && newEng !== curEng;

    if (!nameChanged && !this.imageChanged() && !phoneChanged && !areaChanged
        && !empChanged && !engChanged) {
      // Nada que guardar
      this.dialogRef.close(false);
      return;
    }

    this.isSaving.set(true);
    try {
      await this.usersService.updateCurrentUser({
        ...(nameChanged  ? { name: newName }                       : {}),
        // En BD guardamos el PATH (key S3), NO la presigned URL.
        // Así no caduca y al cargar se firma de nuevo cada vez.
        ...(this.imageChanged() ? { image: this.imagePath ?? null } : {}),
        ...(phoneChanged ? { phone: newPhone || null }              : {}),
        ...(areaChanged ? { area: newArea || null }                 : {}),
        ...(empChanged ? { employeeNumber: newEmp || null }         : {}),
        ...(engChanged ? { engineerNumber: newEng || null }         : {}),
      });
      this.savedAny = true;
      await this.messageService.open({
        title: '¡Listo!', type: 'success',
        message: 'Tu perfil se actualizó correctamente.',
        duration: 2000,
      });
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar.';
      await this.messageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  onClose(): void {
    this.dialogRef.close(this.savedAny);
  }

  /** Iniciales para el avatar fallback */
  initials = computed(() => {
    const name = this.user()?.name ?? '?';
    return name.trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
  });

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }
}
